import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useTranslation, UseTranslationResponse } from 'react-i18next';
import i18n, { normalizeLanguageCode } from '../../i18n';

type TranslationContextType = {
  translation: UseTranslationResponse<'translation', undefined>['t']; // Alias `t` as `translation`
  currentLocale: string;
};
const TranslationContext = createContext<TranslationContextType | null>(null);

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const { t } = useTranslation<'translation', undefined>();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const handleBrowserLanguageChange = () => {
      const browserLanguage = navigator.language;
      const normalizedLanguage = normalizeLanguageCode(browserLanguage);
      if (normalizedLanguage !== currentLanguage) {
        i18n.changeLanguage(normalizedLanguage);
      }
    };

    // Listen for browser language changes (manual changes by user in settings)
    window.addEventListener('languagechange', handleBrowserLanguageChange);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('languagechange', handleBrowserLanguageChange);
    };
  }, [currentLanguage]);

  return (
    <TranslationContext.Provider value={{ translation: t, currentLocale: currentLanguage }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslationContext = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslationContext must be used within a TranslationProvider');
  }
  return context;
};
